// Shared scroll-lock utility used by every drawer/overlay component.
//
// WHY THIS IS NOT AN ES MODULE
//
// It used to export `lockScroll`/`unlockScroll`/`trapFocus`, and each component
// pulled them in with `import './scroll-lock.js'`. That relative specifier
// resolves against the importing file's CDN URL, which drops the `?v=` cache
// fingerprint Shopify appends via `asset_url`. Shopify serves theme assets with:
//
//     Cache-Control: public, max-age=31557600      (one year, verified)
//
// on the versioned AND the unversioned URL. So the unversioned copy fetched by a
// relative import is cached for a year with nothing able to bust it: every edit
// to this file was invisible to any browser that had loaded the page once. Four
// rounds of scroll-lock fixes never reached the storefront.
//
// So it is a plain script now, loaded once by layout/theme.liquid through
// `asset_url` — a versioned URL that changes whenever this file changes — and
// published on `window`. Components read `window.ThemeScrollLock` at call time,
// so load order cannot matter either.
//
// Do not reintroduce a relative `import` of this file, and do not reintroduce a
// second <script> tag for it: two URLs for the same source means two copies of
// `holders` toggling one html class, and whichever syncs last wins.
(function () {
  if (window.ThemeScrollLock) return;

  // Tracks WHO holds the lock rather than counting calls. A plain counter meant
  // an overlay that locked twice — the cart drawer does, because re-rendering
  // calls open() again while already open — needed two unlocks to release the
  // page, so closing it once left the body permanently unscrollable. Keyed by
  // holder, a second lock from the same component is a no-op, and one unlock
  // always releases it.
  const holders = new Set();

  // Every overlay allowed to hold the page still. The lock is only ever
  // legitimate while one of these is actually on screen.
  const OPEN_OVERLAY =
    '[data-drawer-panel][data-open="true"], [data-mobile-menu][data-open="true"]';

  let verifyQueued = false;

  function sync() {
    document.documentElement.classList.toggle('scroll-locked', holders.size > 0);
    scheduleVerify();
  }

  // Self-healing net over the bookkeeping above.
  //
  // A Set of holders is still bookkeeping, and bookkeeping leaks: an exception
  // thrown between lock and unlock, a panel replaced by a fetch re-render while
  // open, a component whose close() never runs. Any leak strands the visitor on
  // a page that cannot scroll with nothing on screen to close. So the DOM gets
  // the final say: once the dust settles, if no overlay is actually open the
  // lock is released no matter what the Set believes.
  function verify() {
    verifyQueued = false;
    if (holders.size === 0) return;
    if (!document.querySelector(OPEN_OVERLAY)) unlockAllScroll();
  }

  function scheduleVerify() {
    if (verifyQueued || holders.size === 0) return;
    verifyQueued = true;
    // Next frame, so a component that sets data-open right after locking (or
    // swaps its whole panel out and back in) is judged on its settled state.
    requestAnimationFrame(verify);
  }

  function lockScroll(holder) {
    holders.add(holder ?? 'anonymous');
    sync();
  }

  function unlockScroll(holder) {
    holders.delete(holder ?? 'anonymous');
    sync();
  }

  function unlockAllScroll() {
    holders.clear();
    sync();
  }

  function trapFocus(container, initialFocusEl) {
    const focusableSelector =
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

    function handleKeydown(event) {
      if (event.key !== 'Tab') return;
      const focusable = Array.from(container.querySelectorAll(focusableSelector)).filter(
        (el) => el.offsetParent !== null
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', handleKeydown);
    (initialFocusEl || container).focus();

    return () => container.removeEventListener('keydown', handleKeydown);
  }

  // A link inside an open drawer navigates without anyone calling close(), so
  // the page unloads with the lock still held. Coming back through the browser's
  // back button restores that document from the bfcache — html class included —
  // and the visitor lands on a page that cannot scroll and has no drawer to
  // close. Clear the lock on every restore; any drawer still open re-locks when
  // it is next opened.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) unlockAllScroll();
  });

  // Re-check whenever an overlay's open state changes or a panel is added or
  // removed — a drawer whose markup is replaced mid-open would otherwise take
  // its data-open="true" with it and leave the lock behind.
  function observe() {
    if (!('MutationObserver' in window) || !document.body) return;
    new MutationObserver(scheduleVerify).observe(document.body, {
      attributes: true,
      attributeFilter: ['data-open'],
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }

  window.ThemeScrollLock = { lockScroll, unlockScroll, unlockAllScroll, trapFocus };
})();
